import type { ApolloError } from "@apollo/client"
import { useParams } from "react-router-dom"
import { useNavigate, useSearchParams } from "@/v2-backoffice-client/utils"
import TranslationSidebar from "@/v2-backoffice-client/translation/sidebar/TranslationSidebar"
import TranslationCatalogTable from "@/v2-backoffice-client/translation/catalog/TranslationCatalogTable"
import TranslationMissingTable from "@/v2-backoffice-client/translation/missing/TranslationMissingTable"
import TranslationFallbackView from "@/v2-backoffice-client/translation/fallback/TranslationFallbackView"
import TranslationDuplicatesView from "@/v2-backoffice-client/translation/duplicates/TranslationDuplicatesView"
import TranslationAddObjectsView from "@/v2-backoffice-client/translation/addObjects/TranslationAddObjectsView"
import TranslationLanguagesView from "@/v2-backoffice-client/translation/languages/TranslationLanguagesView"
import TranslationCountriesView from "@/v2-backoffice-client/translation/countries/TranslationCountriesView"
import TranslationPendingView from "@/v2-backoffice-client/translation/pending/TranslationPendingView"
import { useTranslationPublishRunsQuery } from "@/v2-backoffice-client/translation/publish/translation.publishRuns.query"
import { useCatalogChangesQuery } from "@/v2-backoffice-client/translation/publish/translation.changes.query"
import {
  CURRENT_PUBLISH_ENVIRONMENT,
  findPublishBaseline,
} from "@/v2-backoffice-client/translation/publish/translation.publish.utils"
import { useTranslationMissingQuery } from "@/v2-backoffice-client/translation/missing/translation.missing.query"
import { useTranslationOrphanedQuery } from "@/v2-backoffice-client/translation/orphaned/translation.orphaned.query"
import { useTranslationFallbackQuery } from "@/v2-backoffice-client/translation/fallback/translation.fallback.query"
import TranslationOrphanedTable from "@/v2-backoffice-client/translation/orphaned/TranslationOrphanedTable"
import { getTranslationCategoryHref } from "@/v2-backoffice-client/translation/translation.utils"
import type { TranslationBoard as TranslationBoardData } from "@/v2-backoffice-client/translation/translation.types"
import {
  TRANSLATION_CATEGORY_PARAM,
  TRANSLATION_VIEWS,
  type TranslationView,
} from "@/v2-backoffice-client/translation/translation.constants"

export type TranslationBoardProps = {
  // Owned by TranslationPage (its header's Add Text button needs the same
  // board/refetch), threaded down rather than fetched again here.
  board: TranslationBoardData
  loading: boolean
  error?: ApolloError
  // Resolves when the catalog refetch lands, so post-save navigation (the
  // Add-type-values flow) can wait for the board to carry the new rows.
  onSaved(): Promise<unknown>
}

// Two-column board: the sidebar selects a pinned view or a catalog
// category; the right panel replaces. Selection is URL-addressable —
// /translation/<view> for the pinned views (bare /translation =
// "Missing translations", the triage queue),
// /translation/catalog?category=<key> for a category slice of the catalog.
function TranslationBoard(props: TranslationBoardProps) {
  const { board, loading, error, onSaved } = props
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const view: TranslationView = (
    TRANSLATION_VIEWS as readonly string[]
  ).includes(params.view ?? "")
    ? (params.view as TranslationView)
    : "incomplete"
  const categoryKey = searchParams.get(TRANSLATION_CATEGORY_PARAM)

  // Cheap and cached: gives the sidebar its drift counts (missing in DB /
  // missing in code / pending sync), and the matching views reuse the cache
  // entries on open.
  const { data: missingData } = useTranslationMissingQuery()
  const { data: orphanedData } = useTranslationOrphanedQuery()
  const { data: fallbackData } = useTranslationFallbackQuery()

  // Publish baseline: the last successful publish to the environment this
  // backoffice deployment reads, so the sidebar count answers "what is not
  // yet shipped HERE". The publish dialog re-derives it against whichever
  // environment the operator is targeting; both share findPublishBaseline.
  const { data: runsData } = useTranslationPublishRunsQuery()
  const baseline = findPublishBaseline(
    runsData?.getTranslationPublishRuns.Runs ?? [],
    CURRENT_PUBLISH_ENVIRONMENT,
  )
  const pendingSince = baseline?.CreatedAt ?? null
  const { data: pendingData } = useCatalogChangesQuery({
    variables: { since: pendingSince ?? "" },
    skip: !pendingSince,
  })
  const pendingCount = pendingSince
    ? (pendingData?.getCatalogChangesSince.length ?? null)
    : null

  // After "Add type values" saves a whole set successfully, land the
  // operator on that set's catalog slice so they can verify it without
  // searching for it by hand. Refetch first so the slice isn't empty on
  // arrival — the new rows aren't in the board until the catalog reloads.
  // The one navigation here that can't be a link: it has to wait for a save.
  async function openSeededTypeSet(typeSet: string) {
    await onSaved()
    // Drop the current params (keepParams=false) so the destination carries
    // only its own category.
    navigate(getTranslationCategoryHref(typeSet), undefined, false)
  }

  // The catalog is the one surface that also shows deleted rows (behind its
  // own Status filter, default: Active) — every other view (Missing
  // translations, sidebar badges, duplicates, ...) reads board.rows /
  // incompleteRows directly and stays deleted-row-free.
  const deletedCatalogRows = categoryKey
    ? board.deletedRows.filter((row) => row.categoryKey === categoryKey)
    : board.deletedRows
  const catalogRows = [
    ...(categoryKey
      ? (board.categoryByKey.get(categoryKey)?.rows ?? [])
      : board.rows),
    ...deletedCatalogRows,
  ]

  return (
    <div className="translation-board-split">
      <TranslationSidebar
        board={board}
        categoriesLoading={loading}
        missingInCodeCount={missingData?.getMissingInCodeKeys.length ?? null}
        orphanedCount={orphanedData?.getFeaturesMissingCodeTexts.length ?? null}
        fallbackMismatchCount={
          fallbackData?.getCodeTextMismatches.length ?? null
        }
        pendingPublishCount={pendingCount}
        view={view}
        categoryKey={categoryKey}
      />

      <div className="translation-board-main">
        {view === "catalog" ? (
          <TranslationCatalogTable
            rows={catalogRows}
            categoryByKey={board.categoryByKey}
            languages={board.languages}
            loading={loading}
            error={error}
            onSaved={onSaved}
          />
        ) : view === "incomplete" ? (
          <TranslationCatalogTable
            rows={board.incompleteRows}
            categoryByKey={board.categoryByKey}
            languages={board.languages}
            loading={loading}
            error={error}
            onSaved={onSaved}
          />
        ) : view === "missing" ? (
          <TranslationMissingTable />
        ) : view === "orphaned" ? (
          <TranslationOrphanedTable />
        ) : view === "fallback" ? (
          <TranslationFallbackView />
        ) : view === "duplicates" ? (
          <TranslationDuplicatesView board={board} loading={loading} />
        ) : view === "languages" ? (
          <TranslationLanguagesView />
        ) : view === "countries" ? (
          <TranslationCountriesView />
        ) : view === "pending" ? (
          <TranslationPendingView
            board={board}
            languages={board.languages}
            since={pendingSince}
            sinceLabel={
              baseline
                ? baseline.CreatedAt.replace("T", " ").replace("Z", "")
                : null
            }
            onSaved={onSaved}
          />
        ) : (
          <TranslationAddObjectsView onSeeded={openSeededTypeSet} />
        )}
      </div>
    </div>
  )
}

export default TranslationBoard
